import { isIncome, isPayment } from "./expense";
import { formatCents } from "./format";
import { getBalanceInCents, toCents } from "./money";

export const getBalanceLabel = (cents) => {
  const value = toCents(cents);
  const label = (status, verb, amount) => ({
    status,
    verb,
    amount,
    text: amount ? `${verb} ${amount}` : verb,
  });
  if (value > 0) return label("positive", "bekommt", formatCents(value));
  if (value < 0) return label("negative", "schuldet", formatCents(-value));
  return label("settled", "ausgeglichen", "");
};

const emptyComponents = () => ({
  paid: 0,
  share: 0,
  sent: 0,
  received: 0,
  collected: 0,
  refunded: 0,
});

const componentFields = (expense) => {
  if (isPayment(expense)) return { payer: "sent", receiver: "received" };
  if (isIncome(expense)) return { payer: "collected", receiver: "refunded" };
  return { payer: "paid", receiver: "share" };
};

// paid − share + sent − received − collected + refunded equals the server
// balance gets − pays.
export const getBalanceComponents = (group) => {
  const { participants = [], expenses = [] } = group || {};
  const result = Object.fromEntries(
    participants.map((p) => [p.id, emptyComponents()]),
  );
  const of = (id) => (result[id] ??= emptyComponents());

  for (const expense of expenses) {
    const fields = componentFields(expense);
    of(expense.payer)[fields.payer] += toCents(expense.amount_in_decimals);
    for (const receiver of expense.receivers || []) {
      of(receiver.participant_id)[fields.receiver] += toCents(
        receiver.absolute_share_in_decimals,
      );
    }
  }
  return result;
};

export const getBalanceDetail = ({ paid, sent, received, collected }) => {
  const parts = [];
  if (paid) parts.push(`hat ${formatCents(paid)} bezahlt`);
  if (sent) parts.push(`hat ${formatCents(sent)} überwiesen`);
  if (received) parts.push(`hat ${formatCents(received)} erhalten`);
  if (collected) parts.push(`hat ${formatCents(collected)} eingenommen`);
  return parts.join(" · ");
};

const personalLead = {
  positive: "Du bekommst",
  negative: "Du schuldest",
  settled: "Du bist ausgeglichen",
};

export const getPersonalSummary = (participant) => {
  if (!participant) return null;
  const { status, amount } = getBalanceLabel(getBalanceInCents(participant));
  const lead = personalLead[status];
  return { status, lead, amount, text: amount ? `${lead} ${amount}` : lead };
};

export const getGroupTotals = (group) => {
  const { participants = [], expenses = [] } = group || {};
  const sumOf = (entries) =>
    entries.reduce((sum, e) => sum + toCents(e.amount_in_decimals), 0);
  const onlyExpenses = expenses.filter((e) => !isPayment(e) && !isIncome(e));
  const incomes = expenses.filter(isIncome);
  const hasEntries = expenses.length > 0;
  const allSettled =
    hasEntries && participants.every((p) => getBalanceInCents(p) === 0);
  return {
    totalExpenses: sumOf(onlyExpenses),
    expenseCount: onlyExpenses.length,
    totalIncome: sumOf(incomes),
    incomeCount: incomes.length,
    hasEntries,
    allSettled,
  };
};
